import React, { useEffect, useRef, useState } from 'react';
import Table from 'react-bootstrap/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Pagination from '@mui/material/Pagination';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import CircularProgress from '@mui/material/CircularProgress';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import { NavLink, useNavigate } from 'react-router-dom';
import { postRequest } from '../../../lib/api';
import toast from 'react-hot-toast';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';

const LeadSubstatus = () => {
  const [statusFilter, setStatusFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [showAll, setShowAll] = useState('Show all');
  const [leadData, setLeadData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const debounceRef = useRef();

  const fetchData = async (
    searchTerm = nameFilter,
    parentStatus = statusFilter,
    pageVal = page,
    activeStatus = showAll
  ) => {
    setLoading(true);
    try {
      const payload = {
        status: searchTerm,
        parent_status: parentStatus,
        is_active:'Show all',
        isDeleted: 0,
        page: pageVal,
        limit: 10,
      };

    if (activeStatus !== 'Show all') {
  payload.is_active = activeStatus; // keep it as string: 'true' / 'false'
}


      const res = await postRequest('private/statusAndSubStatusList', payload);

      if (res?.status === 200 && res?.data?.docs) {
        let filtered = res.data.docs;
        setLeadData(filtered);
        setTotalPages(res.data.totalPages || 1);
        setTotalDocs(res.data.totalDocs || 0);
      } else {
        setLeadData([]);
        setTotalPages(1);
        setTotalDocs(0);
      }
    } catch (err) {
      console.error('API Error:', err);
      setLeadData([]);
      setTotalPages(1);
      setTotalDocs(0);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id) => {
  try {
    const res = await postRequest(`private/changeStatusSubstatusToggle/${id}`);
    if (res?.status === 200) {
      toast.success(res.message || 'Status updated');

      // Update local state instead of refetching all data
      setLeadData((prev) =>
        prev.map((item) =>
          item._id === id
            ? { ...item, is_active: item.is_active === 'true' ? 'false' : 'true' }
            : item
        )
      );
    } else {
      toast.error(res?.message || 'Failed to update status');
    }
  } catch (err) {
    console.error(err);
    toast.error('API error');
  }
};


  useEffect(() => {
    fetchData();
  }, [nameFilter, statusFilter, page, showAll]);

  useEffect(() => {
    setPage(1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchData(nameFilter, statusFilter, 1, showAll);
    }, 400);
  }, [nameFilter, statusFilter, showAll]);

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'StatusSubStatus', path: '' },
        ]}
      />
      <br />
      <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
        <div className='page-header pb-3' style={{ display: 'flex', justifyContent: 'space-between' }}>
          <h1 className='mt-0 mb-0'>Lead Sub-Statuses</h1>
          <Stack spacing={2} direction="row">
            <Button variant="outlined" className='red-outline-btn text-capitalize'>
              <NavLink to="/system-setup/LeadCreateSubStatuses" className="red-link-text">
                <PersonAddOutlinedIcon /> &nbsp; Add New Lead Sub-Status
              </NavLink>
            </Button>
          </Stack>
        </div>

        <div style={{ margin: '20px 0' }}>
          <Stack direction="row" spacing={2} style={{ marginBottom: '20px' }}>
            <FormControl fullWidth>
              <InputLabel>Parent Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Parent Status"
              >
                <MenuItem value="">-- Any --</MenuItem>
                <MenuItem value="Quote Sent">Quote Sent</MenuItem>
                <MenuItem value="Instructed">Instructed</MenuItem>
                <MenuItem value="Exported">Exported</MenuItem>
                <MenuItem value="Not Interested">Not Interested</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Sub-Status Name"
              variant="outlined"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />

            <FormControl fullWidth>
              <InputLabel>Filter</InputLabel>
              <Select
                value={showAll}
                onChange={(e) => setShowAll(e.target.value)}
                label="Filter"
              >
                <MenuItem value="Show all">Show all</MenuItem>
                <MenuItem value="true">Active only</MenuItem>
                <MenuItem value="false">Inactive only</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </div>

        <Table striped responsive bordered>
          <thead>
            <tr>
              <th>Parent Status</th>
              <th>Sub-Status</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center' }}>
                  <CircularProgress size={28} />
                </td>
              </tr>
            ) : leadData.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center' }}>
                  No sub-statuses found.
                </td>
              </tr>
            ) : (
              leadData.map((row, index) => (
                <tr key={index}>
                  <td>{row.parent_status}</td>
                  <td>{row.status}</td>
                  <td>
                    {row.is_active === true || row.is_active === 'true' ? 
                      <CheckIcon style={{ width: "1.1rem", color: 'green' }} /> : 
                      <CloseIcon style={{ width: "1.1rem", color: 'red' }} />
                    }
                  </td>
                  <td>
                    <span
                      className="red-link-text"
                      style={{ marginRight: 10, cursor: 'pointer' }}
                      onClick={() => handleToggleStatus(row._id)}
                    >
                      (toggle)
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {leadData.length > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 8, marginBottom: 8 }}>
              <span className="text-muted small">
                Showing {((page - 1) * 10) + 1}-{Math.min(page * 10, totalDocs)} of {totalDocs} lead sub-statuses
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                shape="rounded"
                size="medium"
                sx={{
                  '& .Mui-selected': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important',
                    color: '#fff !important',
                  },
                  '& .MuiPaginationItem-root': {
                    color: 'var(--bs-red-color)',
                    borderColor: 'var(--bs-red-color)',
                  },
                }}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default LeadSubstatus;
